import React, { useState } from 'react';
import moment from 'moment';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    FormGroup,
    Typography,
    Box,
    TextField,
    Divider
} from '@mui/material';

const TripResetDialog = ({ open, onClose, onSubmit }) => {
    const [selectionType, setSelectionType] = useState('include');
    const [selectedStages, setSelectedStages] = useState([]);
    const [useDateRange, setUseDateRange] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const tripStages = ['Planned', 'Active', 'Start Delayed', 'Completed', 'Cancelled', 'Aborted'];

    const handleStageToggle = (stage) => {
        setSelectedStages(prev =>
            prev.includes(stage)
                ? prev.filter(s => s !== stage)
                : [...prev, stage]
        );
    };

    const handleSubmit = () => {
        const payload = {};

        if (selectedStages.length > 0) {
            if (selectionType === 'include') {
                payload.includeTripStages = selectedStages;
            } else {
                payload.excludeTripStages = selectedStages;
            }
        }

        if (useDateRange && startDate && endDate) {
            payload.dateRange = {
                startDate: moment(startDate).startOf('day').format(),
                endDate: moment(endDate).endOf('day').format()
            };
        }

        onSubmit(payload);
        handleReset();
    };

    const handleReset = () => {
        setSelectionType('include');
        setSelectedStages([]);
        setUseDateRange(false);
        setStartDate('');
        setEndDate('');
        onClose();
    };

    const isDateRangeValid = () => {
        if (!useDateRange) return true;
        if (!startDate || !endDate) return false;
        return moment(endDate).isSameOrAfter(moment(startDate));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Reset Trips</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Selection Type
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={selectionType}
                            onChange={(e) => {
                                setSelectionType(e.target.value);
                                setSelectedStages([]);
                            }}
                        >
                            <FormControlLabel
                                value="include"
                                control={<Radio />}
                                label="Include selected stages"
                            />
                            <FormControlLabel
                                value="exclude"
                                control={<Radio />}
                                label="Exclude selected stages"
                            />
                        </RadioGroup>
                    </FormControl>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        Trip Stages
                    </Typography>
                    <FormGroup>
                        {tripStages.map((stage) => (
                            <FormControlLabel
                                key={stage}
                                control={
                                    <Checkbox
                                        checked={selectedStages.includes(stage)}
                                        onChange={() => handleStageToggle(stage)}
                                    />
                                }
                                label={stage}
                            />
                        ))}
                    </FormGroup>

                    <Divider sx={{ my: 2 }} />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useDateRange}
                                onChange={(e) => setUseDateRange(e.target.checked)}
                            />
                        }
                        label="Use Date Range"
                    />

                    {useDateRange && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                            <TextField
                                type="date"
                                label="Start Date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={useDateRange && !startDate}
                                helperText={useDateRange && !startDate ? "Required" : ""}
                            />
                            <TextField
                                type="date"
                                label="End Date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={useDateRange && (!endDate || !isDateRangeValid())}
                                helperText={useDateRange && (!endDate ? "Required" : !isDateRangeValid() ? "End date must be after start date" : "")}
                                inputProps={{
                                    min: startDate
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={selectedStages.length === 0 || !isDateRangeValid()}
                >
                    Reset Trips
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TripResetDialog; 